import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { CURRENCY_CODE } from '../../config/brand';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { IndianRupee, Plus, Pencil, Trash2, Tags, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPricing() {
  const [breeds, setBreeds] = useState([]);
  const [rules, setRules] = useState([]);
  const [taxConfigs, setTaxConfigs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [breedDialog, setBreedDialog] = useState(false);
  const [ruleDialog, setRuleDialog] = useState(false);
  const [taxDialog, setTaxDialog] = useState(false);
  const [editingBreed, setEditingBreed] = useState(null);
  const [editingRule, setEditingRule] = useState(null);

  const [breedForm, setBreedForm] = useState({ name: '', species: 'dog', size_category: 'medium', coat_type: '', grooming_difficulty: 'medium' });
  const [ruleForm, setRuleForm] = useState({ service_id: '', breed: '', species: 'dog', price_override: 0, duration_override: 0, notes: '' });
  const [taxForm, setTaxForm] = useState({ name: 'GST', rate: 18, type: 'GST', is_default: true });

  const fetchAll = async () => {
    try {
      const [b, r, t, s] = await Promise.all([
        api.get('/pricing/breeds'),
        api.get('/pricing/rules'),
        api.get('/pricing/tax'),
        api.get('/services'),
      ]);
      setBreeds(b.data);
      setRules(r.data);
      setTaxConfigs(t.data);
      setServices(s.data.filter(x => !x.is_addon));
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  // Breed CRUD
  const openBreedDialog = (breed = null) => {
    setEditingBreed(breed);
    setBreedForm(breed ? { name: breed.name, species: breed.species, size_category: breed.size_category, coat_type: breed.coat_type, grooming_difficulty: breed.grooming_difficulty } : { name: '', species: 'dog', size_category: 'medium', coat_type: '', grooming_difficulty: 'medium' });
    setBreedDialog(true);
  };
  const saveBreed = async () => {
    try {
      if (editingBreed) await api.put(`/pricing/breeds/${editingBreed.breed_id}`, breedForm);
      else await api.post('/pricing/breeds', breedForm);
      toast.success(editingBreed ? 'Breed updated' : 'Breed created');
      setBreedDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteBreed = async (id) => {
    if (!window.confirm('Delete this breed?')) return;
    await api.delete(`/pricing/breeds/${id}`);
    toast.success('Breed deleted');
    fetchAll();
  };

  // Rule CRUD
  const openRuleDialog = (rule = null) => {
    setEditingRule(rule);
    setRuleForm(rule ? { service_id: rule.service_id, breed: rule.breed, species: rule.species, price_override: rule.price_override, duration_override: rule.duration_override, notes: rule.notes || '' } : { service_id: '', breed: '', species: 'dog', price_override: 0, duration_override: 0, notes: '' });
    setRuleDialog(true);
  };
  const saveRule = async () => {
    try {
      if (editingRule) await api.put(`/pricing/rules/${editingRule.rule_id}`, ruleForm);
      else await api.post('/pricing/rules', ruleForm);
      toast.success(editingRule ? 'Rule updated' : 'Rule created');
      setRuleDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    await api.delete(`/pricing/rules/${id}`);
    toast.success('Rule deleted');
    fetchAll();
  };

  // Tax CRUD
  const saveTax = async () => {
    try {
      await api.post('/pricing/tax', taxForm);
      toast.success('Tax config created');
      setTaxDialog(false);
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-pricing-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Pricing Manager</h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Manage breeds, pricing rules, and tax configuration</p>
      </div>

      <Tabs defaultValue="rules" className="space-y-4">
        <TabsList className="bg-[#F5F5F0] rounded-full p-1 inline-flex">
          <TabsTrigger value="rules" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-5" data-testid="pricing-rules-tab">Pricing Rules ({rules.length})</TabsTrigger>
          <TabsTrigger value="breeds" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-5" data-testid="pricing-breeds-tab">Breeds ({breeds.length})</TabsTrigger>
          <TabsTrigger value="tax" className="rounded-full data-[state=active]:bg-[#2D2D2D] data-[state=active]:text-white text-sm px-5" data-testid="pricing-tax-tab">Tax Config</TabsTrigger>
        </TabsList>

        {/* Pricing Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => openRuleDialog()} data-testid="add-rule-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Rule
            </Button>
          </div>
          <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9F9F7]">
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Service</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Breed</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Base Price</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Override Price</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Duration</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map(r => (
                  <TableRow key={r.rule_id} data-testid={`rule-row-${r.rule_id}`}>
                    <TableCell className="text-sm font-medium">{r.service_name}</TableCell>
                    <TableCell className="text-sm">{r.breed} <span className="text-xs text-[#888]">({r.species})</span></TableCell>
                    <TableCell className="text-sm text-[#888]">&#8377;{r.base_price}</TableCell>
                    <TableCell className="text-sm font-semibold text-[#E07A5F]">&#8377;{r.price_override}</TableCell>
                    <TableCell className="text-sm">{r.duration_override || r.base_duration} min</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => openRuleDialog(r)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]" data-testid={`edit-rule-${r.rule_id}`}><Pencil className="w-3.5 h-3.5 text-[#888]" /></button>
                        <button onClick={() => deleteRule(r.rule_id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-rule-${r.rule_id}`}><Trash2 className="w-3.5 h-3.5 text-[#D9534F]" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Breeds Tab */}
        <TabsContent value="breeds" className="space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => openBreedDialog()} data-testid="add-breed-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Breed
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {breeds.map(b => (
              <Card key={b.breed_id} className="rounded-2xl border-[#E5E5E0] bg-white card-hover" data-testid={`breed-card-${b.breed_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-[#2D2D2D] text-sm">{b.name}</h3>
                      <p className="text-xs text-[#888] capitalize">{b.species} | {b.size_category}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openBreedDialog(b)} className="p-1 rounded hover:bg-[#F5F5F0]"><Pencil className="w-3.5 h-3.5 text-[#888]" /></button>
                      <button onClick={() => deleteBreed(b.breed_id)} className="p-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-[#D9534F]" /></button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {b.coat_type && <span className="text-xs bg-[#F5F5F0] px-2 py-0.5 rounded capitalize">{b.coat_type}</span>}
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${b.grooming_difficulty === 'high' ? 'bg-red-50 text-red-700' : b.grooming_difficulty === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>{b.grooming_difficulty}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tax Tab */}
        <TabsContent value="tax" className="space-y-4">
          <div className="flex justify-end">
            <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => setTaxDialog(true)} data-testid="add-tax-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Tax Config
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {taxConfigs.map(t => (
              <Card key={t.config_id} className="rounded-2xl border-[#E5E5E0] bg-white" data-testid={`tax-card-${t.config_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-[#E07A5F]/10 rounded-xl flex items-center justify-center">
                      <IndianRupee className="w-5 h-5 text-[#E07A5F]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2D2D2D]">{t.name}</h3>
                      <p className="text-2xl font-bold text-[#E07A5F]">{t.rate}%</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {t.is_default && <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded">Default</span>}
                    <span className={`text-xs px-2 py-0.5 rounded ${t.active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{t.active ? 'Active' : 'Inactive'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Breed Dialog */}
      <Dialog open={breedDialog} onOpenChange={setBreedDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{editingBreed ? 'Edit Breed' : 'Add Breed'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Breed Name *</Label><Input value={breedForm.name} onChange={e => setBreedForm({ ...breedForm, name: e.target.value })} className="mt-1 rounded-lg" data-testid="breed-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Species</Label>
                <Select value={breedForm.species} onValueChange={v => setBreedForm({ ...breedForm, species: v })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="dog">Dog</SelectItem><SelectItem value="cat">Cat</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Size</Label>
                <Select value={breedForm.size_category} onValueChange={v => setBreedForm({ ...breedForm, size_category: v })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="small">Small</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="large">Large</SelectItem><SelectItem value="extra_large">Extra Large</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Coat Type</Label><Input value={breedForm.coat_type} onChange={e => setBreedForm({ ...breedForm, coat_type: e.target.value })} className="mt-1 rounded-lg" placeholder="e.g. long, short, curly" /></div>
              <div><Label className="text-sm">Grooming Difficulty</Label>
                <Select value={breedForm.grooming_difficulty} onValueChange={v => setBreedForm({ ...breedForm, grooming_difficulty: v })}>
                  <SelectTrigger className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveBreed} data-testid="save-breed-btn">{editingBreed ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Rule Dialog */}
      <Dialog open={ruleDialog} onOpenChange={setRuleDialog}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{editingRule ? 'Edit Pricing Rule' : 'Add Pricing Rule'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Service *</Label>
              <Select value={ruleForm.service_id} onValueChange={v => setRuleForm({ ...ruleForm, service_id: v })}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="rule-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>{services.map(s => <SelectItem key={s.service_id} value={s.service_id}>{s.name} (&#8377;{s.price})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Breed *</Label>
              <Select value={ruleForm.breed} onValueChange={v => setRuleForm({ ...ruleForm, breed: v })}>
                <SelectTrigger className="mt-1 rounded-lg" data-testid="rule-breed-select"><SelectValue placeholder="Select breed" /></SelectTrigger>
                <SelectContent>{breeds.map(b => <SelectItem key={b.breed_id} value={b.name}>{b.name} ({b.species})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-sm">Override Price ({CURRENCY_CODE})</Label><Input type="number" step="0.01" value={ruleForm.price_override} onChange={e => setRuleForm({ ...ruleForm, price_override: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-lg" data-testid="rule-price-input" /></div>
              <div><Label className="text-sm">Override Duration (min)</Label><Input type="number" value={ruleForm.duration_override} onChange={e => setRuleForm({ ...ruleForm, duration_override: parseInt(e.target.value) || 0 })} className="mt-1 rounded-lg" data-testid="rule-duration-input" /></div>
            </div>
            <div><Label className="text-sm">Notes</Label><Input value={ruleForm.notes} onChange={e => setRuleForm({ ...ruleForm, notes: e.target.value })} className="mt-1 rounded-lg" /></div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveRule} data-testid="save-rule-btn">{editingRule ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tax Dialog */}
      <Dialog open={taxDialog} onOpenChange={setTaxDialog}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add Tax Configuration</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Name</Label><Input value={taxForm.name} onChange={e => setTaxForm({ ...taxForm, name: e.target.value })} className="mt-1 rounded-lg" data-testid="tax-name-input" /></div>
            <div><Label className="text-sm">Rate (%)</Label><Input type="number" step="0.1" value={taxForm.rate} onChange={e => setTaxForm({ ...taxForm, rate: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-lg" data-testid="tax-rate-input" /></div>
          </div>
          <DialogFooter><Button className="rounded-full bg-[#2D2D2D] text-white" onClick={saveTax} data-testid="save-tax-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
